import type { HTMLAttributes, ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

/**
 * Badge — BRANDBOOK §6.4 (Sprint 28c hed_v3, C2A karar).
 *
 * İki sınıf:
 *   - shape='pill' (default, 999px) → STATUS badge'leri (Onaylandı/Bekliyor/Reddedildi).
 *     Mono uppercase, subtle bg + fg-2-shade text.
 *   - shape='label' (4px rect)      → NON-status label/chip (nav sayaç, "Yeni",
 *     filtre chip, generic count). Sans, ink bg veya lime bg.
 *
 * Tone (pill için anlamlı):
 *   success / warning / danger / info / neutral / accent (lime).
 */

const badgeVariants = cva('inline-flex items-center gap-1 font-medium whitespace-nowrap', {
  variants: {
    shape: {
      pill: 'rounded-custel-pill font-mono uppercase tracking-[0.04em] text-[11px] px-2.5 py-1',
      label: 'rounded-custel-sm text-[11px] px-2 py-0.5',
    },
    tone: {
      neutral: 'bg-custel-g100 text-custel-g700',
      accent: 'bg-custel-ink text-custel-lime',
      success: 'bg-custel-success-subtle text-[color:var(--custel-successFg)]',
      warning: 'bg-custel-warning-subtle text-[color:var(--custel-warningFg)]',
      danger: 'bg-custel-danger-subtle text-[color:var(--custel-dangerFg)]',
      info: 'bg-custel-info-subtle text-[color:var(--custel-infoFg)]',
      outline: 'border border-custel-border text-custel-g700 bg-transparent',
    },
    size: {
      sm: 'text-[10px] px-1.5 py-0',
      md: '', // shape default'u kullan
    },
  },
  compoundVariants: [
    // label shape + accent → lime bg + ink text (sidebar sayaç vurgusu)
    { shape: 'label', tone: 'accent', class: 'bg-custel-lime text-custel-ink' },
    // label shape + neutral → ink bg + krem text (default nav sayaç)
    { shape: 'label', tone: 'neutral', class: 'bg-custel-ink text-custel-krem' },
  ],
  defaultVariants: { shape: 'pill', tone: 'neutral', size: 'md' },
});

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  leadingIcon?: ReactNode;
}

export function Badge({ className, shape, tone, size, leadingIcon, children, ...rest }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ shape, tone, size }), className)} {...rest}>
      {leadingIcon && <span aria-hidden="true">{leadingIcon}</span>}
      {children}
    </span>
  );
}
